import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, DocumentData } from 'firebase/firestore';
import { db } from '../../config/firebase'; // Adjust path as needed
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAuth, ROLE_ADMIN } from '../../contexts/AuthContext'; // Adjust path as needed
import { Link } from 'react-router-dom';
import ProjectFormModal from '../../components/admin/ProjectFormModal'; // Added import

// Interfaces (in English)
interface Project {
    id: string;
    name: string;
    clientId: string;
}

interface Client {
    id: string;
    name: string;
    // Add other client fields if needed for display or logic
}

// Component (in English)
export default function ProjectsPage() {
    const { user, userHasRole } = useAuth();
    const [projects, setProjects] = useState<Project[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingClients, setLoadingClients] = useState(true); // Added for consistency with modal
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false); // Added state for modal
    const [editingProject, setEditingProject] = useState<Project | null>(null); // Added state for editing

    const canViewPage = useMemo(() => {
        if (!user) return false;
        return userHasRole(ROLE_ADMIN); // Assuming only ADMIN can manage projects
    }, [user, userHasRole]);

    // Fetch Projects and Clients
    useEffect(() => {
        if (!canViewPage) {
            setLoading(false);
            setError("Você não tem permissão para visualizar esta página.");
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            setLoadingClients(true); // Start loading clients
            try {
                // Fetch Projects
                const projectsCollection = collection(db, 'projects'); // Explicitly define collection for new projects
                const projectsSnapshot = await getDocs(projectsCollection);
                const projectsList = projectsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as Project[];
                setProjects(projectsList);

                // Fetch Clients (to map clientId to client name)
                const clientsSnapshot = await getDocs(collection(db, 'clients'));
                const clientsList = clientsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as Client[];
                setClients(clientsList);
                setLoadingClients(false); // Finish loading clients

                setError(null);
            } catch (err) {
                console.error("Error fetching data:", err);
                setError("Falha ao carregar dados. Verifique o console para mais detalhes.");
                setLoadingClients(false); // Also set to false on error
            }
            setLoading(false);
        };

        fetchData();
    }, [canViewPage]);

    const getClientNameById = (clientId: string): string => {
        const client = clients.find(c => c.id === clientId);
        return client ? client.name : 'Cliente não encontrado';
    };

    const handleAddProject = () => {
        setEditingProject(null);
        setIsModalOpen(true);
    };

    const handleEditProject = (project: Project) => {
        setEditingProject(project);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingProject(null);
        setError(null); // Clear any form-related errors shown on the page
    };

    const handleFormSubmit = async (
        projectModalData: { name: string; clientId?: string; id?: string },
        newClientName?: string
    ) => {
        setError(null); // Clear previous page errors
        let currentClientId = projectModalData.clientId;

        try {
            // If a new client name is provided, create the client first
            if (newClientName && newClientName.trim() !== '') {
                const clientsCollection = collection(db, 'clients');
                // Check if client already exists by name (optional, good practice)
                const existingClient = clients.find(c => c.name.toLowerCase() === newClientName.trim().toLowerCase());
                if (existingClient) {
                    // Decide how to handle: use existing, or throw error. For now, use existing.
                    // Or, throw an error to be caught by the modal: throw new Error("Um cliente com este nome já existe.");
                    currentClientId = existingClient.id;
                    // Optionally, update clients state if not already perfectly in sync, though find should work.
                } else {
                    const newClientDocData = { name: newClientName.trim() };
                    const clientDocRef = await addDoc(clientsCollection, newClientDocData as DocumentData);
                    currentClientId = clientDocRef.id;
                    const newClient = { id: clientDocRef.id, ...newClientDocData };
                    setClients(prevClients => [...prevClients, newClient]);
                }
            }

            if (!currentClientId) {
                // This should ideally be caught by modal validation if not creating new client
                throw new Error("Client ID is missing and no new client was specified.");
            }

            const projectDataForFirestore: Omit<Project, 'id'> = {
                name: projectModalData.name,
                clientId: currentClientId, // Use the determined client ID
            };

            if (projectModalData.id && editingProject) { // Check editingProject to be sure it's an update context
                // Update existing project
                const projectDocRef = doc(db, 'projects', projectModalData.id);
                await updateDoc(projectDocRef, projectDataForFirestore as DocumentData);
                setProjects(prevProjects =>
                    prevProjects.map(p =>
                        p.id === projectModalData.id ? { ...p, ...projectDataForFirestore, id: projectModalData.id! } : p
                    )
                );
            } else {
                // Add new project
                const projectsCollection = collection(db, 'projects');
                const docRef = await addDoc(projectsCollection, projectDataForFirestore as DocumentData);
                setProjects(prevProjects => [...prevProjects, { ...projectDataForFirestore, id: docRef.id }]);
            }
            handleModalClose();
        } catch (err: any) {
            console.error("Error saving project:", err);
            // This error will be re-thrown to be caught by the modal's error handler.
            // setError("Falha ao salvar projeto. Verifique o console para detalhes."); // Page level error
            throw err;
        }
    };

    const handleDeleteProject = async (projectId: string) => {
        if (window.confirm("Tem certeza que deseja excluir este projeto?")) {
            try {
                await deleteDoc(doc(db, 'projects', projectId));
                setProjects(prevProjects => prevProjects.filter(p => p.id !== projectId));
                // Optionally, show a success message
            } catch (err) {
                console.error("Error deleting project:", err);
                setError("Falha ao excluir projeto.");
                // Optionally, show an error message to the user
            }
        }
    };

    if (!user && loading) { // Still loading user auth state
        return <div className="p-4 text-gray-500">Carregando informações do usuário...</div>;
    }

    if (!canViewPage && !loading) { // User is loaded, but has no permission
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-center p-4">
                    <h1 className="text-2xl font-semibold text-gray-700 mb-4">Acesso Negado</h1>
                    <p className="text-gray-500">{error || "Você não tem permissão para visualizar esta página."}</p>
                    <Link to="/" className="mt-6 inline-block bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700">
                        Voltar para o Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (loading) {
        return <div className="p-4 text-gray-500">Carregando projetos...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="sm:flex sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                        Gerenciar Projetos
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Adicione, edite ou remova projetos.
                    </p>
                </div>
                <div className="mt-4 sm:mt-0">
                    <button
                        type="button"
                        onClick={handleAddProject}
                        className="btn-primary"
                    >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Adicionar Novo Projeto
                    </button>
                </div>
            </div>

            {error && (
                <div className="rounded-md bg-red-50 p-4 my-4">
                    <div className="flex">
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Erro</h3>
                            <div className="mt-2 text-sm text-red-700">
                                <p>{error}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="overflow-x-auto bg-white shadow sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome do Projeto</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                            <th scope="col" className="relative px-6 py-3">
                                <span className="sr-only">Ações</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {projects.length === 0 && !loading ? (
                            <tr>
                                <td colSpan={3} className="px-6 py-12 text-center text-sm text-gray-500">
                                    Nenhum projeto cadastrado.
                                </td>
                            </tr>
                        ) : (
                            projects.map((project) => (
                                <tr key={project.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{project.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{getClientNameById(project.clientId)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                        <button onClick={() => handleEditProject(project)} className="text-indigo-600 hover:text-indigo-900" title="Editar Projeto">
                                            <PencilIcon className="h-5 w-5" />
                                        </button>
                                        <button onClick={() => handleDeleteProject(project.id)} className="text-red-600 hover:text-red-900" title="Excluir Projeto">
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* TODO: Add Modal for Project Form here */}
            <ProjectFormModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onSubmit={handleFormSubmit}
                initialProjectData={editingProject}
                clients={clients}
                loadingClients={loadingClients}
            />
        </div>
    );
} 